/* Copy do inbox de suporte. A toolbar de filtros (`InboxFilters`) é um componente à parte —
   fora do escopo desta migração — e mantém os próprios literais (status/prioridade/atribuição/
   período/origem) intocados aqui. */
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

interface SupportInboxContent {
  readonly pageTitle: string;
  readonly pageDescription: string;
  readonly loadError: string;
  readonly loadMoreButton: string;
  readonly bulk: {
    readonly selectedCount: (count: number) => string;
    readonly assign: string;
    readonly close: string;
    readonly clear: string;
    readonly selectPage: string;
    readonly selectRow: (subject: string) => string;
    readonly assignTitle: string;
    readonly assignDescription: (count: number) => string;
    readonly assignConfirm: string;
    readonly closeTitle: string;
    readonly closeDescription: (count: number) => string;
    readonly closeReasonLabel: string;
    readonly closeReasonPlaceholder: string;
    readonly closeReasonError: string;
    readonly closeConfirm: string;
    readonly success: (count: number) => string;
    readonly partial: (success: number, failed: number) => string;
    readonly error: string;
  };
}

const SUPPORT_INBOX_CONTENT: Bilingual<SupportInboxContent> = {
  'pt-BR': {
    pageTitle: 'Inbox de suporte',
    pageDescription: 'Tickets abertos pelos usuários — triagem e atendimento.',
    loadError: 'Falha ao carregar tickets.',
    loadMoreButton: 'Carregar mais',
    bulk: {
      selectedCount: (count) => `${count} ticket(s) selecionado(s)`,
      assign: 'Atender selecionados',
      close: 'Fechar selecionados',
      clear: 'Limpar seleção',
      selectPage: 'Selecionar tickets ativos desta página',
      selectRow: (subject) => `Selecionar ticket ${subject}`,
      assignTitle: 'Atender tickets selecionados',
      assignDescription: (count) =>
        `Atribuir ${count} ticket(s) ativo(s) a você e movê-los para em andamento?`,
      assignConfirm: 'Atender',
      closeTitle: 'Fechar tickets selecionados',
      closeDescription: (count) =>
        `Fechar ${count} ticket(s) ativo(s) usando o mesmo motivo? Tickets já fechados serão recusados.`,
      closeReasonLabel: 'Motivo do fechamento',
      closeReasonPlaceholder: 'Descreva a resolução (mín. 10 caracteres)',
      closeReasonError: 'Informe um motivo com pelo menos 10 caracteres.',
      closeConfirm: 'Fechar tickets',
      success: (count) => `${count} ticket(s) atualizado(s).`,
      partial: (success, failed) => `${success} atualizado(s); ${failed} com falha.`,
      error: 'Falha ao aplicar a ação em lote.',
    },
  },
  'en-US': {
    pageTitle: 'Support inbox',
    pageDescription: 'Tickets opened by users — triage and handling.',
    loadError: 'Failed to load tickets.',
    loadMoreButton: 'Load more',
    bulk: {
      selectedCount: (count) => `${count} ticket(s) selected`,
      assign: 'Handle selected',
      close: 'Close selected',
      clear: 'Clear selection',
      selectPage: 'Select active tickets on this page',
      selectRow: (subject) => `Select ticket ${subject}`,
      assignTitle: 'Handle selected tickets',
      assignDescription: (count) =>
        `Assign ${count} active ticket(s) to you and move them to in progress?`,
      assignConfirm: 'Handle',
      closeTitle: 'Close selected tickets',
      closeDescription: (count) =>
        `Close ${count} active ticket(s) with the same reason? Already closed tickets will be rejected.`,
      closeReasonLabel: 'Closing reason',
      closeReasonPlaceholder: 'Describe the resolution (at least 10 characters)',
      closeReasonError: 'Enter a reason with at least 10 characters.',
      closeConfirm: 'Close tickets',
      success: (count) => `${count} ticket(s) updated.`,
      partial: (success, failed) => `${success} updated; ${failed} failed.`,
      error: 'Failed to apply the bulk action.',
    },
  },
};

export const useSupportInboxContent = (): SupportInboxContent =>
  useBilingual(SUPPORT_INBOX_CONTENT);
